/*
 * © 2026 eggs unimedia GmbH
 * Coded with love 💛
 */

import { signIn, signUp, confirmSignUp, resendSignUpCode, signOut, fetchAuthSession, getCurrentUser, autoSignIn, updateUserAttributes, resetPassword, confirmResetPassword } from 'aws-amplify/auth';

export const amplifyAuthService = {
  async login(email, password) {
    const { isSignedIn, nextStep } = await signIn({ username: email, password });
    if (!isSignedIn) {
      if (nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        const err = new Error('User is not confirmed');
        err.name = 'UserNotConfirmedException';
        throw err;
      }
      throw new Error('Login failed');
    }
    const user = await getCurrentUser();
    return { email: user.username };
  },

  async register(email, password, name, consentData) {
    const { nextStep } = await signUp({
      username: email,
      password,
      options: {
        userAttributes: { email, name },
        autoSignIn: true,
      },
    });

    // Auto-Confirm path: Pre-Signup Lambda confirms + verifies email
    if (nextStep.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
      // P4: wrap autoSignIn in try/catch — user is created even if this fails
      try {
        await autoSignIn();
      } catch (e) {
        console.error('Auto sign-in failed after signup:', e);
        // User was created but auto-sign-in failed — they can still log in manually
        return { email, nextStep: 'AUTO_SIGN_IN_FAILED' };
      }

      // P5: consent attributes non-blocking — don't break registration if this fails
      if (consentData) {
        try {
          await updateUserAttributes({
            userAttributes: {
              'custom:consentAt': consentData.consentAt,
              'custom:consentVersion': consentData.consentVersion,
              'custom:consentScope': JSON.stringify(consentData.consentScope),
            },
          });
        } catch (e) {
          console.error('Failed to store consent attributes:', e);
        }
      }
    }

    return { email, nextStep: nextStep.signUpStep };
  },

  async confirm(email, code) {
    await confirmSignUp({ username: email, confirmationCode: code });
    return { success: true };
  },

  async resendCode(email) {
    await resendSignUpCode({ username: email });
    return { success: true };
  },

  async logout() {
    await signOut();
  },

  async isAuthenticated() {
    try {
      const session = await fetchAuthSession();
      return !!session.tokens?.idToken;
    } catch {
      return false;
    }
  },

  async getCurrentUser() {
    try {
      const user = await getCurrentUser();
      return user.username;
    } catch {
      return null;
    }
  },

  async forgotPassword(email) {
    await resetPassword({ username: email });
  },

  async confirmForgotPassword(email, code, newPassword) {
    await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
  },

  async deleteAccount() {
    const { deleteUser } = await import('aws-amplify/auth')
    await deleteUser()
  },
};
